import React from 'react';
import { i18n } from '@international/mastodon-i18n';
import { File } from 'components/Workflow/Disputes/Types';
import { FILE_STACK_API_KEY } from '../../constants';
import FilestackCustomRender from './FilestackCustomRender';
import SecureFilestack from '../SecureFilestack/SecureFilestack';
import useFilestackOptions from './hooks/useFIlestackOptions';

import './FilestackUpload.scss';

export interface CustomUploadProps {
  buttonText?:
    | {
        default: string;
        more?: string;
      }
    | string;
  labelText: string;
  options?: any;
  disabled?: boolean;
  errorMessage?: string;
  initialFiles?: File[];
  onChange?: (event: { value: File[] }) => void;
}

const CustomUpload: React.FC<CustomUploadProps> = ({
  buttonText = '',
  labelText,
  options = {},
  onChange = () => {},
  disabled,
  errorMessage = '',
  initialFiles = [],
}) => {
  const [uploadedFiles, setUploadedFiles] = React.useState<any[]>(() =>
    Array.isArray(initialFiles) ? initialFiles : [],
  );
  const [newFiles, setNewFiles] = React.useState(false);
  const [error, setError] = React.useState('');

  const onRemoveHandler = (fileName: string) => {
    setUploadedFiles(prevUploadedFiles =>
      prevUploadedFiles.reduce<any[]>((result, currentFile) => {
        if (currentFile.filename === fileName) return result;
        return [...result, currentFile];
      }, []),
    );
    setNewFiles(true);
  };
  const fileStackOptions = useFilestackOptions(options);
  // `replaceOnMax` is a candidate-portal-only upload option, not a FileStack
  // picker option — pull it out so it isn't forwarded to the picker.
  const { replaceOnMax, ...actionOptions } = fileStackOptions;

  const CustomRender = React.useCallback(
    ({ onPick }: any) => (
      <FilestackCustomRender
        onClick={onPick}
        uploadedFiles={uploadedFiles}
        maxFiles={fileStackOptions.maxFiles}
        labelText={labelText}
        buttonText={buttonText}
        onRemove={onRemoveHandler}
        disabled={disabled}
        errorMessage={error}
      />
    ),
    [uploadedFiles, labelText, buttonText, disabled, fileStackOptions, error],
  );

  React.useEffect(() => {
    if (newFiles) {
      setNewFiles(false);
      onChange({ value: uploadedFiles });
    }
  }, [newFiles, onChange, uploadedFiles]);

  React.useEffect(() => {
    if (errorMessage === 'validations.requiredDocuments.one') {
      setError(
        i18n.getStr(
          `components.Verifications.IdDocumentVerification.inputErrorMessage.required`,
          2,
        ),
      );
    }
  }, [errorMessage]);

  return (
    <div
      className={`file-uploader${
        error || errorMessage ? ' file-uploader--error' : ''
      }`}
    >
      <SecureFilestack
        apikey={FILE_STACK_API_KEY}
        actionOptions={actionOptions}
        componentDisplayMode={{
          type: 'immediate',
        }}
        customRender={CustomRender}
        onSuccess={(response: any) => {
          setUploadedFiles(prevFiles => {
            // Single-slot fields (e.g. DEE's supporting document, maxFiles: 1)
            // opt into replaceOnMax: a new upload swaps in for the current
            // file(s) instead of hitting the limit error, so the candidate can
            // re-upload without deleting first.
            if (replaceOnMax) {
              setError('');
              return response.filesUploaded.slice(-fileStackOptions.maxFiles);
            }
            const result = [...prevFiles, ...response.filesUploaded];
            if (result.length > fileStackOptions.maxFiles)
              setError(
                i18n.getStr(
                  `components.Verifications.IdDocumentVerification.inputErrorMessage.maxFiles`,
                  fileStackOptions.maxFiles,
                ),
              );
            return result.slice(0, fileStackOptions.maxFiles);
          });
          setNewFiles(true);
        }}
      />
    </div>
  );
};

export default CustomUpload;
